import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Search, X, Gift } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PhoneInputWithCode } from "@/components/ui/phone-input-with-code";
import { useCreatePatient, useCheckDuplicatePatient } from "@/hooks/usePatients";
import { useCreateReferral } from "@/hooks/usePatientReferrals";
import { useSmilePointsConfig, useAwardPoints } from "@/hooks/useSmilePoints";
import { toast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/useDebounce";
import { DuplicatePatientWarning } from "./DuplicatePatientWarning";

// Minimal schema for quick patient creation with 10-digit validation
const quickPatientSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  gender: z.enum(["male", "female", "other"], { required_error: "Gender is required" }),
  phone: z.string()
    .min(1, "Phone number is required")
    .refine((val) => {
      const digits = val.replace(/\D/g, '');
      const localDigits = digits.length > 10 ? digits.slice(-10) : digits;
      return localDigits.length === 10;
    }, { message: "Phone number must be exactly 10 digits" }),
  registration_number: z.string(),
});

type QuickPatientFormData = z.infer<typeof quickPatientSchema>;

interface QuickCreatePatientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (patientId: string) => void;
  clinicId?: string;
  defaultPhone?: string;
}

export const QuickCreatePatientDialog = ({
  open,
  onOpenChange,
  onSuccess,
  clinicId,
  defaultPhone = "",
}: QuickCreatePatientDialogProps) => {
  const navigate = useNavigate();
  const [duplicatePatient, setDuplicatePatient] = useState<any>(null);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  
  // Referral search state
  const [referralSearch, setReferralSearch] = useState("");
  const [referralResults, setReferralResults] = useState<any[]>([]);
  const [selectedReferrer, setSelectedReferrer] = useState<any>(null);
  const [showReferralDropdown, setShowReferralDropdown] = useState(false);
  const debouncedReferralSearch = useDebounce(referralSearch, 300);

  // Title case helper
  const toTitleCase = (str: string) =>
    str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

  const form = useForm<QuickPatientFormData>({
    resolver: zodResolver(quickPatientSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      gender: undefined as any,
      phone: defaultPhone,
      registration_number: "AUTO",
    },
  });

  // Set default phone on mount
  useEffect(() => {
    if (open && defaultPhone) {
      form.setValue("phone", defaultPhone);
    }
  }, [open, defaultPhone]);

  // Search for referrer patients
  useEffect(() => {
    const searchReferrers = async () => {
      if (debouncedReferralSearch.length < 2) {
        setReferralResults([]);
        return;
      }
      
      const { data } = await supabase
        .from("patients")
        .select("id, name, phone, registration_number")
        .or(`name.ilike.%${debouncedReferralSearch}%,phone.ilike.%${debouncedReferralSearch}%,registration_number.ilike.%${debouncedReferralSearch}%`)
        .limit(5);
      
      setReferralResults(data || []);
      setShowReferralDropdown(true);
    };
    
    searchReferrers();
  }, [debouncedReferralSearch]);

  const createPatientMutation = useCreatePatient();
  const checkDuplicateMutation = useCheckDuplicatePatient();
  const createReferralMutation = useCreateReferral();
  const awardPointsMutation = useAwardPoints();
  const { data: smilePointsConfig } = useSmilePointsConfig();

  const handleSubmit = async (data: QuickPatientFormData) => {
    if (!clinicId || clinicId.trim() === "") {
      toast({
        title: "Error",
        description: "Clinic information is missing.",
        variant: "destructive",
      });
      return;
    }

    try {
      const fullName = `${data.first_name} ${data.last_name}`.trim();

      if (!fullName || fullName.length < 2) {
        toast({
          title: "Error",
          description: "Please enter both first and last name.",
          variant: "destructive",
        });
        return;
      }

      const duplicateResult = await checkDuplicateMutation.mutateAsync({
        name: fullName,
        phone: data.phone,
        clinicId: clinicId,
      });

      if (duplicateResult && duplicateResult.length > 0) {
        setDuplicatePatient(duplicateResult[0]);
        setShowDuplicateWarning(true);
      } else {
        await createPatient(data);
      }
    } catch (error: any) {
      console.error("Patient creation error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create patient.",
        variant: "destructive",
      });
    }
  };

  const createPatient = async (data: QuickPatientFormData) => {
    const patient = await createPatientMutation.mutateAsync({
      clinic_id: clinicId!,
      first_name: data.first_name,
      last_name: data.last_name,
      gender: data.gender,
      phone: data.phone,
      registration_number: data.registration_number,
      registration_complete: false,
      referred_by_patient_id: selectedReferrer?.id || null,
    });

    // Create referral record and award points if there's a referrer
    if (selectedReferrer) {
      try {
        await createReferralMutation.mutateAsync({
          referringPatientId: selectedReferrer.id,
          referredPatientId: patient.id,
          clinicId: clinicId!,
        });

        // Award points to referring patient
        const bonusPoints = smilePointsConfig?.referral_bonus_points || 500;
        await awardPointsMutation.mutateAsync({
          patientId: selectedReferrer.id,
          clinicId: clinicId!,
          points: bonusPoints,
          transactionType: "earned_referral",
          referenceType: "referral",
          referenceId: patient.id,
          description: `Referral bonus for referring ${data.first_name} ${data.last_name}`,
        });

        toast({
          title: "Patient Created",
          description: `Quick registration complete. ${bonusPoints} points awarded to ${selectedReferrer.name}!`,
        });
      } catch (referralError) {
        console.error("Referral/points error:", referralError);
        toast({
          title: "Patient Created",
          description: `Quick registration complete. Note: Referral points may need manual verification.`,
        });
      }
    } else {
      toast({
        title: "Patient Created",
        description: "Quick registration complete. Full details will be collected at check-in.",
      });
    }

    form.reset();
    setShowDuplicateWarning(false);
    setDuplicatePatient(null);
    setSelectedReferrer(null);
    setReferralSearch("");
    onOpenChange(false);
    onSuccess?.(patient.id);
  };

  const handleViewExistingPatient = () => {
    if (duplicatePatient) {
      form.reset();
      setShowDuplicateWarning(false);
      setDuplicatePatient(null);
      setSelectedReferrer(null);
      setReferralSearch("");
      onOpenChange(false);
      // Navigate to patient profile page
      navigate(`/patients/${duplicatePatient.id}`);
    }
  };

  const handleCreateAnyway = async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      setShowDuplicateWarning(false);
      setDuplicatePatient(null);
      const currentValues = form.getValues();
      await createPatient(currentValues);
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditDetails = () => {
    // Go back to form editing
    setShowDuplicateWarning(false);
  };

  const selectReferrer = (patient: any) => {
    setSelectedReferrer(patient);
    setReferralSearch("");
    setShowReferralDropdown(false);
    setReferralResults([]);
  };

  const clearReferrer = () => {
    setSelectedReferrer(null);
    setReferralSearch("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Quick Patient Registration</DialogTitle>
          <DialogDescription>
            Enter minimal details for phone booking. Full registration at check-in.
          </DialogDescription>
        </DialogHeader>

        {showDuplicateWarning && duplicatePatient ? (
          <DuplicatePatientWarning
            duplicatePatient={duplicatePatient}
            onViewExisting={handleViewExistingPatient}
            onCreateAnyway={handleCreateAnyway}
            onEditDetails={handleEditDetails}
          />
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="First name"
                          {...field}
                          onBlur={(e) => {
                            field.onBlur();
                            field.onChange(toTitleCase(e.target.value));
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Last name"
                          {...field}
                          onBlur={(e) => {
                            field.onBlur();
                            field.onChange(toTitleCase(e.target.value));
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="gender"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Gender *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number *</FormLabel>
                    <FormControl>
                      <PhoneInputWithCode
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Phone number"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Registration number is auto-generated by database trigger */}

              {/* Referred By Field */}
              <div className="space-y-2 p-3 border rounded-lg bg-muted/30">
                <div className="flex items-center gap-2">
                  <Gift className="h-4 w-4 text-amber-500" />
                  <FormLabel className="text-sm">Referred By (Optional)</FormLabel>
                  <span className="text-xs text-muted-foreground">(Referrer gets {smilePointsConfig?.referral_bonus_points || 500} points!)</span>
                </div>
                {selectedReferrer ? (
                  <div className="flex items-center justify-between p-2 border rounded-md bg-muted/50">
                    <div>
                      <p className="font-medium text-sm">{selectedReferrer.name}</p>
                      <p className="text-xs text-muted-foreground">{selectedReferrer.registration_number}</p>
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={clearReferrer}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search patient by name or phone..."
                      value={referralSearch}
                      onChange={(e) => setReferralSearch(e.target.value)}
                      className="pl-9"
                    />
                    {showReferralDropdown && referralResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-48 overflow-auto">
                        {referralResults.map((patient) => (
                          <button
                            key={patient.id}
                            type="button"
                            className="w-full px-3 py-2 text-left hover:bg-accent text-sm"
                            onClick={() => selectReferrer(patient)}
                          >
                            <p className="font-medium">{patient.name}</p>
                            <p className="text-xs text-muted-foreground">{patient.registration_number}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createPatientMutation.isPending}>
                  {createPatientMutation.isPending ? "Creating..." : "Create Patient"}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
